import { defaultPRNG, mulberry32, type PRNG } from "./prng";
import { loadSource } from "./registry";
import type { LoremGenerateOpts, LoremSourceName } from "./types";

const DEFAULT_MIN_SENTENCES = 2;
const DEFAULT_MAX_SENTENCES = 5;
const DEFAULT_MIN_WORDS = 30;
const DEFAULT_MAX_WORDS = 80;

/**
 * Generate `count` paragraphs from the named source. Returns an array
 * of paragraph strings (no leading/trailing whitespace).
 *
 * Engineering brief §C6 contract:
 *   - Each paragraph: 2-5 sentences, total 30-80 words (configurable
 *     via `opts.min/maxSentences` + `opts.min/maxWords`).
 *   - Optional `opts.seed` for deterministic test output.
 *   - Source corpus is lazy-loaded on first call.
 *
 * Throws if the source isn't registered (`sources/index.ts` bootstraps
 * the built-in sources; user-registered sources fire `registerSource`
 * directly).
 */
export async function generate(
	source: LoremSourceName,
	count: number,
	opts: LoremGenerateOpts = {},
): Promise<string[]> {
	if (count <= 0) return [];

	const corpus = await loadSource(source);
	if (corpus.sentences.length === 0) {
		throw new Error(`Lorem source "${source}" has an empty corpus`);
	}

	const minSentences = opts.minSentences ?? DEFAULT_MIN_SENTENCES;
	const maxSentences = opts.maxSentences ?? DEFAULT_MAX_SENTENCES;
	const minWords = opts.minWords ?? DEFAULT_MIN_WORDS;
	const maxWords = opts.maxWords ?? DEFAULT_MAX_WORDS;
	const rng: PRNG =
		opts.seed === undefined ? defaultPRNG() : mulberry32(opts.seed);

	const out: string[] = [];
	for (let i = 0; i < count; i++) {
		out.push(
			buildParagraph(corpus.sentences, rng, {
				minSentences,
				maxSentences,
				minWords,
				maxWords,
			}),
		);
	}
	return out;
}

interface BuildOpts {
	minSentences: number;
	maxSentences: number;
	minWords: number;
	maxWords: number;
}

function buildParagraph(
	sentences: readonly string[],
	rng: PRNG,
	opts: BuildOpts,
): string {
	const targetSentenceCount = rng.int(opts.minSentences, opts.maxSentences);
	const picked: string[] = [];
	let wordCount = 0;
	let lastIdx = -1;

	for (let i = 0; i < targetSentenceCount; i++) {
		// Pick non-adjacent sentence to reduce immediate repetition.
		let idx = rng.int(0, sentences.length - 1);
		if (sentences.length > 1 && idx === lastIdx) {
			idx = (idx + 1) % sentences.length;
		}
		lastIdx = idx;
		const sentence = sentences[idx];
		picked.push(sentence);
		wordCount += countWords(sentence);
		if (wordCount >= opts.maxWords) break;
	}

	// Top up to minWords if we fell short.
	let safety = 50;
	while (wordCount < opts.minWords && safety-- > 0) {
		const idx = rng.int(0, sentences.length - 1);
		const sentence = sentences[idx];
		picked.push(sentence);
		wordCount += countWords(sentence);
	}

	return picked.map(stripTrailingPunct).join(". ").concat(".");
}

function countWords(s: string): number {
	if (!s) return 0;
	return s.trim().split(/\s+/).length;
}

function stripTrailingPunct(s: string): string {
	return s.replace(/[.!?]+\s*$/, "");
}
